import { LOCATION_CHANGE, replace } from 'connected-react-router';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  actionReceivePrimaryContent,
  actionReceiveError,
  actionReceiveSiteOptions,
  actionRequestArchive,
  actionRequestContentItem,
  actionRequestSiteOptions,
  actionRequestNextPageFetchInitiated,
} from 'actions';
import { REQUEST_NEXT_PAGE, FETCH_USER_REQUESTED } from 'config/types/action';
import { getContentGroupTypes } from 'selectors';
import getActivePagination from 'selectors/getActivePagination';
import getMatch from 'selectors/getMatch';
import getRedirect from 'selectors/getRedirect';
import hasFetchedActiveContent from 'selectors/hasFetchedActiveContent';
import logger from 'utils/rollbar';
import contentItemMetaSaga from './contentItemMetaSaga';
import { fetchUser } from './userSaga';

/**
 * Dynamic saga to be run after Redux store creation.
 * @param {Object} service remote service implementation
 */
export default function* rootSaga(service) {
  try {
    // Combine all continuous sagas, and run them in parallel.
    yield all([
      takeLatest(LOCATION_CHANGE, watchLocationChanges, service),
      takeLatest(REQUEST_NEXT_PAGE, watchRequestNextPage, service),
      takeLatest(FETCH_USER_REQUESTED, fetchUser, service),
      fork(contentItemMetaSaga, service),
    ]);
  } catch (err) {
    yield put(actionReceiveError(err));
    logger.error('Unexpected error happened in a redux saga.', err);
  }
}

/**
 * Emit site options and related side effects.
 * @param {Object} service remote service implementation
 */
export function* requestSiteOptions(service) {
  let contentGroupTypes = yield select(getContentGroupTypes);
  const hasGroupTypes = contentGroupTypes.every((group) => group.length);
  if (!hasGroupTypes) {
    yield put(actionRequestSiteOptions());
    const siteOptions = yield call(service.fetchSiteOptions);
    yield put(actionReceiveSiteOptions(siteOptions));
  }

  // Make sure the service state has been updated with content group types
  // from site options. If the app was pre-rendered on the server, then the
  // service will be lacking content group types.
  contentGroupTypes = yield select(getContentGroupTypes);
  yield call(service.registerContentGroups, ...contentGroupTypes);
}

/**
 * Emit location change side effects.
 * @param {Object} service remote service implementation
 */
export function* watchLocationChanges(service) {
  const { fetchArchive, fetchContentItem } = service;
  const match = yield select(getMatch);
  const { isArchive } = match;

  const redirect = yield select(getRedirect);

  if (redirect) {
    yield put(replace(redirect));
    return;
  }

  if (yield select(hasFetchedActiveContent)) {
    return;
  }

  if (isArchive) {
    const { perPage, currentPage } = yield select(getActivePagination);
    yield put(actionRequestArchive());
    const content = yield call(fetchArchive, match, perPage, currentPage);
    if (content) {
      content.appSpecificMeta = { hasLocationChanged: true };
    }
    yield put(actionReceivePrimaryContent(content));
  } else {
    yield put(actionRequestContentItem());
    const content = yield call(fetchContentItem, match);
    if (content) {
      content.appSpecificMeta = { hasLocationChanged: true };
    }
    yield put(actionReceivePrimaryContent(content));
  }
}

/**
 * Emit request next page side effects.
 * @param {function} fetchArchive
 */
export function* watchRequestNextPage(service) {
  const { fetchArchive } = service;
  const match = yield select(getMatch);
  const { isArchive } = match;
  if (!isArchive) {
    return;
  }

  if (yield select(hasFetchedActiveContent)) {
    return;
  }

  yield put(actionRequestNextPageFetchInitiated());

  const { perPage, currentPage } = yield select(getActivePagination);
  const content = yield call(fetchArchive, match, perPage, currentPage);
  if (content) {
    content.appSpecificMeta = { hasLocationChanged: false };
  }
  yield put(actionReceivePrimaryContent(content));
}
